import { useState, FormEvent } from 'react';
import PasswordInput from '../PasswordInput';
import { api } from '../../services/api';
import { SendOtpResponse } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
import UsernameAvailability from './UsernameAvailability';

interface RegistrationData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  password: string;
}

interface RegisterModalProps {
  initialData: RegistrationData | null;
  onNext: (data: RegistrationData) => void;
  onClose: () => void;
}

export default function RegisterModal({ initialData, onNext, onClose }: RegisterModalProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    username: initialData?.username || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    password: initialData?.password || '',
    confirmPassword: initialData?.password || '',
  });
  const [usernameValid, setUsernameValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.firstName.trim()) {
      setError(t('auth.firstName.required'));
      return;
    }
    if (!formData.lastName.trim()) {
      setError(t('auth.lastName.required'));
      return;
    }
    if (!formData.username.trim()) {
      setError(t('auth.username.required'));
      return;
    }
    if (!usernameValid) {
      setError(t('auth.username.notReady'));
      return;
    }
    if (!formData.email.trim()) {
      setError(t('auth.email.required'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError(t('auth.email.invalid'));
      return;
    }
    if (!formData.phone.trim()) {
      setError(t('auth.phone.required'));
      return;
    }
    if (formData.password.length < 8) {
      setError(t('auth.password.min'));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.password.mismatch'));
      return;
    }

    setLoading(true);
    try {
      const fullPhone = `971${formData.phone}`;

      // Partner check-availability — backend returns only "username" conflicts for role=GARAGE_OWNER.
      const availability = await api.post<{ available: boolean; conflicts: string[] }>('/api/auth/check-availability', {
        role: 'GARAGE_OWNER',
        username: formData.username.trim().toLowerCase(),
        email: formData.email,
        phone: fullPhone,
      });

      if (!availability.available) {
        const conflicts = availability.conflicts || [];
        if (conflicts.includes('username')) {
          setError(t('auth.username.taken'));
        } else {
          setError(t('auth.accountExists'));
        }
        setLoading(false);
        return;
      }

      await api.post<SendOtpResponse>('/api/notifications/otp/send', {
        phone: fullPhone,
        type: 'REGISTRATION',
      });
      onNext({
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username.trim().toLowerCase(),
        email: formData.email,
        phone: fullPhone,
        password: formData.password,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.sendOtpFailed');
      if (message.includes('409') || message.toLowerCase().includes('already')) {
        setError(t('auth.accountExists'));
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-backdrop" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h1 className="auth-brand-text">{t('auth.brand')}</h1>
          <h2>{t('auth.register.title')}</h2>
          <p>{t('auth.register.subtitle')}</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row-half">
            <div className="form-group">
              <label htmlFor="reg-firstName">{t('auth.firstName')}</label>
              <div className="input-with-icon">
                <svg className="input-icon-left" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <input
                  type="text"
                  id="reg-firstName"
                  className="has-icon-left"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  placeholder={t('auth.firstName.placeholder')}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="reg-lastName">{t('auth.lastName')}</label>
              <div className="input-with-icon">
                <svg className="input-icon-left" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <input
                  type="text"
                  id="reg-lastName"
                  className="has-icon-left"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  placeholder={t('auth.lastName.placeholder')}
                  required
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="reg-username">{t('auth.username')}</label>
            <div className="input-with-icon">
              <svg className="input-icon-left" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <input
                type="text"
                id="reg-username"
                className="has-icon-left"
                value={formData.username}
                onChange={handleChange('username')}
                placeholder={t('auth.username.placeholder')}
                required
                disabled={loading}
                autoComplete="off"
                dir="ltr"
                spellCheck={false}
              />
            </div>
            <UsernameAvailability value={formData.username} onValidityChange={setUsernameValid} />
          </div>

          <div className="form-group">
            <label htmlFor="reg-email">{t('auth.email')}</label>
            <div className="input-with-icon">
              <svg className="input-icon-left" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <input
                type="email"
                id="reg-email"
                className="has-icon-left"
                value={formData.email}
                onChange={handleChange('email')}
                placeholder={t('auth.email.placeholder')}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="reg-phone">{t('auth.phone')}</label>
            <div className="phone-input-group">
              <span className="phone-prefix">
                <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                971
              </span>
              <input
                type="tel"
                id="reg-phone"
                value={formData.phone}
                onChange={handleChange('phone')}
                placeholder={t('auth.phone.placeholder')}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="reg-password">{t('auth.password')}</label>
            <PasswordInput
              id="reg-password"
              value={formData.password}
              onChange={(val) => setFormData(prev => ({ ...prev, password: val }))}
              placeholder={t('auth.password.min')}
              disabled={loading}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="reg-confirmPassword">{t('auth.password.confirm')}</label>
            <PasswordInput
              id="reg-confirmPassword"
              value={formData.confirmPassword}
              onChange={(val) => setFormData(prev => ({ ...prev, confirmPassword: val }))}
              placeholder={t('auth.password.confirm.placeholder')}
              disabled={loading}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('auth.sendingCode') : t('auth.next')}
            </button>
          </div>
        </form>

        <div className="form-footer">
          <p>
            {t('auth.hasAccount')}{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); onClose(); }}>
              {t('auth.backToSignIn')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
